import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import * as actions from "../../../../store/actions/actions";
import UnifiedAPIService, { UnifiedAPI } from "../../../../services/UnifiedAPIService";
import { FormattedMessage } from "react-intl";
import GenericPageHeader from '../../../../legacy-components/page-headers/GenericPageHeader';
import ProxyShareViewAction from "./ProxyShareViewAction";
import ProxyDeleteAction from "./ProxyDeleteAction";
import ProxyEnvironmentView from "./ProxyEnvironmentView";
import Alert from '@material-ui/lab/Alert';

interface ProxySummaryProps {
    proxy: any; // TODO: Define proper type
    dispatch: any;
    match: {
        params: {
            apiName: string;
        }
    };
    history: any;
    proxyThrottlingConfig: any;
}

const ProxySummary: React.FC = () => {
    const history = useHistory();
    const { apiName } = useParams<{ apiName: string }>();
    const proxy = useSelector((state: any) => state.proxyManager?.proxy);
    const proxyThrottlingConfig = useSelector((state: any) => state.proxyManager?.proxyThrottlingConfig);
    const dispatch = useDispatch();
    
    const [state, setState] = useState({
        isApiLoaded: false,
        isProxyThrottlingConfigLoaded: false,
        apis: null,
        tabIndex: 0,
        error: false
    });

    useEffect(() => {
        loadProxy();
        loadProxyThrottlingConfig();

        return () => {
            dispatch({ type: actions.storeProxy, payload: null });
            dispatch({ type: actions.storeProxyThrottlingConfig, payload: null });
        };
    }, [apiName, dispatch]); 

    const loadProxy = () => {
        if (!proxy && !state.isApiLoaded) {
            const request = {
                apiName,
                includeEmailAddress: true
            };
            
            UnifiedAPIService.request({
                url: UnifiedAPI.parseRequestURL(UnifiedAPI.get_team_proxy, request as any),
                method: 'GET'
            })
            .then((response) => {
                setState(prev => ({ 
                    ...prev, 
                    isApiLoaded: true,
                    error: false
                }));
                dispatch({ type: actions.storeProxy, payload: response.data });
            })
            .catch((error) => {
                console.error('Error loading proxy:', error);
                setState(prev => ({ 
                    ...prev, 
                    isApiLoaded: true,
                    error: true 
                }));
            });
        }
    };

    const loadProxyThrottlingConfig = () => {
        if (!proxyThrottlingConfig && !state.isProxyThrottlingConfigLoaded) {
            const request = {
                apiName
            };
            
            UnifiedAPIService.request({
                url: UnifiedAPI.parseRequestURL(UnifiedAPI.get_proxy_throttling_config, request),
                method: 'GET'
            })
            .then((response) => {
                setState(prev => ({ 
                    ...prev, 
                    isProxyThrottlingConfigLoaded: true,
                    error: false
                }));
                dispatch({ type: actions.storeProxyThrottlingConfig, payload: response.data });
            })
            .catch((error) => {
                console.error('Error loading proxy throttling config:', error);
                setState(prev => ({ 
                    ...prev, 
                    isProxyThrottlingConfigLoaded: true 
                }));
            });
        }
    };

    const onDeleteSuccess = () => {
        setTimeout(() => {
            history.push('/home/proxy/');
        }, 5000);
    };

    const reload = () => {
        setState(prev => ({
            ...prev,
            isApiLoaded: false,
            isProxyThrottlingConfigLoaded: false
        }));
        loadProxy();
        loadProxyThrottlingConfig();
    };

    return (
        <>
        <Alert severity="info">API proxies are now managed through the APIs section. Please use the APIs page for all proxy management.</Alert>

            {state.error && 
                <Alert severity='error'>
                    The following proxy is restricted to the following user for the selected team
                </Alert>
            }
            <div className="proxy-summary-header">
                {proxy && 
                    <GenericPageHeader
                        header={<FormattedMessage defaultMessage="API Proxy" />}
                        title={proxy && (proxy.displayName ?? proxy.apiName)}
                        controls={
                            proxy &&
                            <section className="flex-row">
                                <ProxyShareViewAction proxy={proxy} />
                                <ProxyDeleteAction proxy={proxy} onSuccess={onDeleteSuccess} />
                            </section>
                        }
                    />
                }
            </div>
            <div className="description">
                {proxy?.description}
            </div>
            {(proxy && 
                <ProxyEnvironmentView 
                    proxy={proxy} 
                    onUpdate={reload} 
                />
            )}
        </>
    );
};

export default ProxySummary;
